import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { OnboardingWizard } from "../components/OnboardingWizard";
import { Button } from "../components/ui/button";

export default function JoinPsychologist() {
  const { t } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(undefined);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/register?role=professional"); return; }
    if (user.role !== "psychologist") { navigate("/"); return; }
    api.get("/psychologists/me").then((r) => setExisting(r.data)).catch(() => setExisting(null));
  }, [user, loading, navigate]);

  if (loading || existing === undefined) {
    return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">{t("common.loading")}</div>;
  }

  // Verified / suspended profiles are managed from the workspace, not the onboarding flow.
  if (existing && ["verified", "suspended"].includes(existing.status)) {
    navigate("/mi-espacio");
    return null;
  }

  // Already submitted and awaiting review — show a confirmation instead of the editable flow.
  if (existing && existing.status === "pending") {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EBF6F0] text-[#1E6B47]"><CheckCircle2 className="h-8 w-8" /></div>
        <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-slateink" data-testid="pending-title">{t("join.pending_title")}</h1>
        <p className="mt-3 text-muted-foreground">{t("join.pending_desc")}</p>
        <Button onClick={() => navigate("/mi-espacio")} className="mt-8 rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="go-workspace-btn">{t("join.go_workspace")}</Button>
      </div>
    );
  }

  // draft / changes / rejected / no profile yet -> resumable onboarding wizard.
  const startStage = existing?.onboarding_stage != null ? Math.min(6, Math.max(0, parseInt(existing.onboarding_stage, 10) || 0)) : 0;
  return <OnboardingWizard initial={existing || undefined} startStage={startStage} />;
}
